import { useState, useEffect } from 'react';

import useAuthStore from '@entities/auth/model/useAuthStore';

import {
  useGetEmployeesByTeamQuery,
  useGetEmployeeByNameQuery,
} from '@features/employee/hooks/index';
import useGetTeamQuery from '@features/team/hooks';
import Alert from '@shared/components/atoms/Alert';

import OrganizationTree from '@features/document-write/components/OrganizationTree/OrganizationTree';

import styles from './FormRecipientModal.module.scss';

/**
 * FormRecipientModal 컴포넌트
 * - 어드민 전용 참조자/시행자/검토자 선택 모달
 * - RecipientModal을 기반으로 생성
 * - 참조자/시행자/검토자 모두 다중 선택 가능 (최대 20명)
 */
function FormRecipientModal({ isOpen, onClose, onSelect, title, selectedRecipients = [] }) {
  const [selectedTeamCode, setSelectedTeamCode] = useState(null);
  const [searchName, setSearchName] = useState('');
  const [searchTrigger, setSearchTrigger] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState(null);
  const [checkedEmployees, setCheckedEmployees] = useState(new Set());
  const [alertState, setAlertState] = useState({
    isOpen: false,
    message: '',
  });

  const MAX_SELECTED_EMPLOYEES = 20;
  // 검토자 선택 모달일 때는 최대 4명으로 제한
  const MAX_REVIEWERS = 4;
  const isReviewerModal = title === '검토자 선택';
  const maxEmployees = isReviewerModal ? MAX_REVIEWERS : MAX_SELECTED_EMPLOYEES;

  // 모달 오픈 시 배경(body) 스크롤 방지
  useEffect(
    function () {
      if (!isOpen) {
        return undefined;
      }

      const currentCount = Number(document.body.dataset.modalLockCount || '0');
      const nextCount = currentCount + 1;
      document.body.dataset.modalLockCount = String(nextCount);

      if (currentCount === 0) {
        document.body.dataset.modalLockOverflow = document.body.style.overflow || '';
        document.body.dataset.modalLockPaddingRight = document.body.style.paddingRight || '';

        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (scrollBarWidth > 0) {
          document.body.style.paddingRight = `${scrollBarWidth}px`;
        }
      }

      return function () {
        const count = Number(document.body.dataset.modalLockCount || '0');
        const restoredCount = Math.max(0, count - 1);
        document.body.dataset.modalLockCount = String(restoredCount);

        if (restoredCount === 0) {
          document.body.style.overflow = document.body.dataset.modalLockOverflow || '';
          document.body.style.paddingRight = document.body.dataset.modalLockPaddingRight || '';
          delete document.body.dataset.modalLockOverflow;
          delete document.body.dataset.modalLockPaddingRight;
        }
      };
    },
    [isOpen],
  );

  // Alert 표시 함수
  function showAlert(message) {
    setAlertState({
      isOpen: true,
      message,
    });
  }

  // Alert 닫기 함수
  function closeAlert() {
    setAlertState({
      isOpen: false,
      message: '',
    });
  }

  // 회사 코드 가져오기
  const companyCode = useAuthStore((state) => state.companyCode);

  // 팀 목록 조회
  const teamQuery = useGetTeamQuery(companyCode, {
    enabled: isOpen && !!companyCode,
  });
  const teamList = teamQuery.data?.resultData?.teamList || [];

  // 팀별 사원 목록 조회
  const employeeQuery = useGetEmployeesByTeamQuery(selectedTeamCode, {
    enabled: isOpen && !!selectedTeamCode,
  });
  const employees = employeeQuery.data?.resultData?.employeeList || [];

  // 사원 이름 검색
  const employeeNameQuery = useGetEmployeeByNameQuery(searchTrigger, {
    enabled: isOpen && searchTrigger.length >= 1,
  });
  const searchResults = employeeNameQuery.data?.resultData?.employeeList || [];

  // 모달이 열릴 때마다 selectedRecipients를 기반으로 selectedEmployees 초기화
  useEffect(
    function () {
      if (isOpen) {
        const updatedEmployees =
          selectedRecipients && selectedRecipients.length > 0
            ? selectedRecipients.map(function (r) {
                return {
                  enumber: r.enumber || 0,
                  name: r.name || '',
                  position: r.position || '',
                  team: r.team || '',
                };
              })
            : [];
        setSelectedEmployees(updatedEmployees);
        setCheckedEmployees(new Set(updatedEmployees.map((emp) => emp.enumber)));
      } else {
        setSelectedTeamCode(null);
        setSearchName('');
        setSearchTrigger('');
        setSelectedEmployees([]);
        setCheckedEmployees(new Set());
      }
    },
    [isOpen, selectedRecipients],
  );

  // 팀 선택 핸들러
  function handleTeamSelect(teamCode) {
    setSelectedTeamCode(teamCode);
    setSearchName('');
    setSearchTrigger('');
  }

  // 중복 직원 확인
  function checkDuplicateEmployee(employee) {
    return selectedEmployees.some((emp) => emp.enumber === employee.enumber);
  }

  // 체크박스 토글
  function handleCheckboxToggle(employee) {
    const isChecked = checkedEmployees.has(employee.enumber);
    const isInSelected = selectedEmployees.some((emp) => emp.enumber === employee.enumber);

    if (isChecked) {
      setCheckedEmployees(function (prev) {
        const newChecked = new Set(prev);
        newChecked.delete(employee.enumber);
        return newChecked;
      });
      setSelectedEmployees(function (prev) {
        return prev.filter((emp) => emp.enumber !== employee.enumber);
      });
    } else {
      if (isInSelected) {
        setCheckedEmployees(function (prev) {
          const newChecked = new Set(prev);
          newChecked.add(employee.enumber);
          return newChecked;
        });
        return;
      }

      if (selectedEmployees.length >= maxEmployees) {
        showAlert(
          isReviewerModal
            ? `검토자는 최대 ${MAX_REVIEWERS}명까지만 선택할 수 있습니다.`
            : `${maxEmployees}명 이상 추가할 수 없습니다.`,
        );
        return;
      }

      if (checkDuplicateEmployee(employee)) {
        showAlert('이미 선택된 사원입니다.');
        return;
      }

      const newEmployee = {
        enumber: employee.enumber,
        name: employee.name,
        position: employee.position || '',
        team: employee.team || '',
      };

      setCheckedEmployees(function (prev) {
        const newChecked = new Set(prev);
        newChecked.add(employee.enumber);
        return newChecked;
      });
      setSelectedEmployees(function (prev) {
        const alreadyExists = prev.some((emp) => emp.enumber === employee.enumber);
        if (alreadyExists) {
          return prev;
        }
        return [...prev, newEmployee];
      });
    }
  }

  // 직원 리스트에서 드래그 시작
  function handleEmployeeDragStart(e, employee) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify(employee));
    e.target.style.opacity = '0.5';
  }

  // 직원 리스트에서 드래그 종료
  function handleEmployeeDragEnd(e) {
    e.target.style.opacity = '1';
  }

  // 선택 목록 영역에 드롭
  function handleDropToSelectedSection(e) {
    e.preventDefault();
    const employeeData = e.dataTransfer.getData('application/json');
    if (!employeeData) {
      return;
    }

    try {
      const employee = JSON.parse(employeeData);
      if (checkDuplicateEmployee(employee)) {
        return;
      }

      if (selectedEmployees.length >= maxEmployees) {
        showAlert(
          isReviewerModal
            ? `검토자는 최대 ${MAX_REVIEWERS}명까지만 선택할 수 있습니다.`
            : `${maxEmployees}명 이상 추가할 수 없습니다.`,
        );
        return;
      }

      const newEmployee = {
        enumber: employee.enumber,
        name: employee.name,
        position: employee.position || '',
        team: employee.team || '',
      };

      setSelectedEmployees(function (prev) {
        return [...prev, newEmployee];
      });

      setCheckedEmployees(function (prev) {
        const newChecked = new Set(prev);
        newChecked.add(employee.enumber);
        return newChecked;
      });
    } catch (error) {
      console.error('Failed to parse employee data:', error);
    }
  }

  // 선택된 직원 제거
  function handleRemoveEmployee(index) {
    const employeeToRemove = selectedEmployees[index];
    setSelectedEmployees(function (prev) {
      return prev.filter((_, i) => i !== index);
    });
    setCheckedEmployees(function (prev) {
      const newChecked = new Set(prev);
      newChecked.delete(employeeToRemove.enumber);
      return newChecked;
    });
  }

  // 드래그 시작 (순서 변경용)
  function handleDragStart(e, index) {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  }

  // 드래그 종료
  function handleDragEnd(e) {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 드래그 오버 (드롭 가능 영역 표시)
  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  // 카드 위에서 드래그 오버 (위/아래 구분)
  function handleCardDragOver(e, index) {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null) {
      return;
    }

    if (draggedIndex === index) {
      setDragOverIndex(null);
      setDragOverPosition(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const cardMiddle = rect.top + rect.height / 2;

    const position = mouseY < cardMiddle ? 'top' : 'bottom';

    setDragOverIndex(index);
    setDragOverPosition(position);
  }

  // 카드에서 드래그 리브
  function handleCardDragLeave(e) {
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 드롭 (순서 변경)
  function handleDrop(e, dropIndex) {
    e.preventDefault();
    if (draggedIndex === null) {
      return;
    }

    if (draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setDragOverPosition(null);
      return;
    }

    const updated = [...selectedEmployees];
    const draggedItem = updated[draggedIndex];

    updated.splice(draggedIndex, 1);

    let insertIndex = dropIndex;
    if (dragOverPosition === 'bottom') {
      insertIndex = dropIndex + 1;
    }

    updated.splice(insertIndex, 0, draggedItem);

    setSelectedEmployees(updated);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 검색 핸들러
  function handleSearch() {
    setSearchTrigger(searchName);
    setSelectedTeamCode(null);
  }

  // 저장 핸들러
  function handleSave() {
    if (onSelect) {
      onSelect(selectedEmployees);
    }
    onClose();
  }

  // 검색 결과 표시 여부
  const showSearchResults = searchTrigger.length >= 1;
  const displayEmployees = showSearchResults ? searchResults : employees;

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} role="dialog" aria-modal="true">
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{title || '선택'}</h2>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.contentLayout}>
            {/* 왼쪽: 조직도 트리 */}
            <div className={`${styles.columnContainer} ${styles.treeSection}`}>
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>조직도</div>
              </div>
              <div className={styles.treeContent}>
                <OrganizationTree
                  companyName={teamList?.[0]?.company || '링크아이'}
                  teamList={teamList}
                  selectedTeamCode={selectedTeamCode}
                  onSelectTeam={handleTeamSelect}
                  height={510}
                />
              </div>
            </div>

            {/* 가운데: 임직원 목록 */}
            <div className={`${styles.columnContainer} ${styles.employeeSection}`}>
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>임직원</div>
                <div className={styles.searchBlock}>
                  <div className={styles.searchRow}>
                    <div className={styles.searchLabelCell}>이름</div>
                    <div className={styles.searchControlCell}>
                      <input
                        type="text"
                        className={styles.searchInput}
                        style={{ width: '130px' }}
                        value={searchName}
                        onChange={function (e) {
                          setSearchName(e.target.value);
                        }}
                        onKeyDown={function (e) {
                          if (e.key === 'Enter') {
                            handleSearch();
                          }
                        }}
                      />
                      <button type="button" className={styles.smallButton} onClick={handleSearch}>
                        검색
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* 직원 목록 테이블 */}
              <div className={styles.employeeTableBlock}>
                <div className={styles.employeeTableSection}>
                  <table className={styles.employeeTable}>
                    <thead>
                      <tr>
                        <th className={styles.thCenter}>
                          <input
                            type="checkbox"
                            checked={
                              displayEmployees.length > 0 &&
                              displayEmployees.every((emp) => checkedEmployees.has(emp.enumber))
                            }
                            onChange={function (e) {
                              if (e.target.checked) {
                                const newChecked = new Set(checkedEmployees);
                                const newEmployees = [...selectedEmployees];

                                displayEmployees.forEach((emp) => {
                                  if (newChecked.has(emp.enumber)) {
                                    return;
                                  }
                                  if (!checkDuplicateEmployee(emp)) {
                                    if (newEmployees.length < maxEmployees) {
                                      newChecked.add(emp.enumber);
                                      newEmployees.push({
                                        enumber: emp.enumber,
                                        name: emp.name,
                                        position: emp.position || '',
                                        team: emp.team || '',
                                      });
                                    }
                                  } else {
                                    newChecked.add(emp.enumber);
                                  }
                                });
                                setCheckedEmployees(newChecked);
                                setSelectedEmployees(newEmployees);
                              } else {
                                const currentEnumbers = displayEmployees.map((emp) => emp.enumber);
                                setCheckedEmployees(function (prev) {
                                  const next = new Set(prev);
                                  currentEnumbers.forEach(function (num) {
                                    next.delete(num);
                                  });
                                  return next;
                                });
                                setSelectedEmployees(function (prev) {
                                  return prev.filter(
                                    (emp) => !currentEnumbers.includes(emp.enumber),
                                  );
                                });
                              }
                            }}
                          />
                        </th>
                        <th className={styles.thCenter}>부서</th>
                        <th className={styles.thCenter}>직급</th>
                        <th className={styles.thCenter}>이름</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayEmployees.length > 0 ? (
                        displayEmployees.map(function (employee) {
                          return (
                            <tr
                              key={employee.enumber}
                              className={styles.employeeRow}
                              draggable
                              onDragStart={function (e) {
                                handleEmployeeDragStart(e, employee);
                              }}
                              onDragEnd={handleEmployeeDragEnd}
                              onClick={function (e) {
                                if (e.target.type === 'checkbox') {
                                  return;
                                }
                                handleCheckboxToggle(employee);
                              }}
                            >
                              <td className={styles.tdCenter}>
                                <input
                                  type="checkbox"
                                  checked={checkedEmployees.has(employee.enumber)}
                                  onChange={function (e) {
                                    e.stopPropagation();
                                    handleCheckboxToggle(employee);
                                  }}
                                  onClick={function (e) {
                                    e.stopPropagation();
                                  }}
                                />
                              </td>
                              <td className={styles.tdCenter}>{employee.team || '-'}</td>
                              <td className={styles.tdCenter}>{employee.position || '-'}</td>
                              <td className={styles.tdCenter}>{employee.name}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className={styles.emptyCell}>
                            {showSearchResults
                              ? '검색 결과가 없습니다.'
                              : '부서를 선택하거나 검색하세요.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* 오른쪽: 선택된 목록 */}
            <div
              className={`${styles.columnContainer} ${styles.selectedSection}`}
              onDragOver={handleDragOver}
              onDrop={handleDropToSelectedSection}
            >
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>{title?.replace(' 선택', '') || '선택'}</div>
              </div>
              <div className={styles.selectedList}>
                {selectedEmployees.length > 0 ? (
                  selectedEmployees.map(function (employee, index) {
                    const showTopLine =
                      draggedIndex !== null &&
                      dragOverIndex === index &&
                      dragOverPosition === 'top';
                    const showBottomLine =
                      draggedIndex !== null &&
                      dragOverIndex === index &&
                      dragOverPosition === 'bottom';

                    return (
                      <div key={`${employee.enumber}-${index}`} className={styles.cardWrapper}>
                        {showTopLine && <div className={styles.dropIndicator} />}
                        <div
                          className={styles.approverCard}
                          draggable
                          onDragStart={function (e) {
                            handleDragStart(e, index);
                          }}
                          onDragOver={function (e) {
                            handleCardDragOver(e, index);
                          }}
                          onDragLeave={handleCardDragLeave}
                          onDragEnd={handleDragEnd}
                          onDrop={function (e) {
                            handleDrop(e, index);
                          }}
                        >
                          <span className={styles.cardIndex}>{index + 1}</span>
                          <span className={styles.approverName}>{employee.name}</span>
                          <span className={styles.approverTeam}>{employee.team || '-'}</span>
                          <button
                            type="button"
                            className={styles.removeButton}
                            onClick={function () {
                              handleRemoveEmployee(index);
                            }}
                            aria-label="제거"
                          >
                            ✕
                          </button>
                        </div>
                        {showBottomLine && <div className={styles.dropIndicator} />}
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.emptyCell}>선택된 직원이 없습니다.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={`${styles.footerButton} ${styles.btnCancel}`}
            onClick={onClose}
          >
            취소
          </button>
          <button
            type="button"
            className={`${styles.footerButton} ${styles.btnSave}`}
            onClick={handleSave}
          >
            저장
          </button>
        </div>
      </div>

      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
    </div>
  );
}

export default FormRecipientModal;
